'use client';
/**
 * L'ÉCHANGE PAR GLISSER-DÉPOSER — vue Mois du planning (26/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Dans l'affichage par mois, je voudrais pouvoir faire un glisser-déposer.
 *   Je voudrais également, en posant sur une autre personne, qu'il y ait un
 *   échange entre les postes. »                           — RQ, 26/10/2026
 *
 * Même bibliothèque que la semaine (`PlanningDnd`, `@dnd-kit/core`), même
 * principe : VALIDER PENDANT LE GLISSÉ. Les pastilles qui acceptent la dépose
 * sont signalées dès la prise, les autres s'estompent. Le geste est ici
 * différent : la pastille déposée n'ira pas dans une CASE, elle tombera sur une
 * AUTRE PASTILLE — et les deux changent de place.
 *
 * ── Pourquoi un composant à part et non `PlanningDnd` ──────────────────────
 *
 * `PlanningDnd` déplace une affectation vers une case (poste × jour) : la cible
 * est un `<td>`. Ici la cible est une AFFECTATION. Une même pastille est à la
 * fois ce qu'on saisit et ce sur quoi on dépose — deux rôles, un seul élément.
 *
 * ── Ce qui n'est PAS négociable ────────────────────────────────────────────
 *
 *  · l'échange est UNE route serveur, atomique (`POST ged/rh/affectations/
 *    echanger`) — jamais deux écritures lancées d'ici ;
 *  · un refus, du serveur ou de la validation locale, se DIT : le motif
 *    s'affiche. Un glissé refusé en silence ferait croire à une panne ;
 *  · hors mode « Modifier », aucun geste — le mois reste un outil de lecture
 *    tant qu'on ne demande pas explicitement le contraire.
 */
import React from 'react';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, useDraggable, useDroppable,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { I } from '@/components/ui';

/** Une affectation, telle que la pastille la porte. */
export interface PastilleEchange {
  documentId: string;
  date: string;
  posteCode: string;
  orgId: string;
  nom: string;
  posteLibelle: string;
  nature?: string | null;
  derogation?: unknown;
  derogationAbsence?: unknown;
}

export interface ContexteEchange {
  /** Vrai quand le mode « Modifier » est ouvert ET que le profil écrit. */
  actif: boolean;
  /** Déposer `a` sur `b` est-il permis ? Évalué dès la prise, pour toutes les cibles. */
  valide: (a: PastilleEchange, b: PastilleEchange) => { ok: boolean; motif?: string };
  /** Échanger réellement. */
  echanger: (a: PastilleEchange, b: PastilleEchange) => Promise<void>;
  /** Dire pourquoi une dépose est refusée (le glissé ne l'envoie pas au serveur). */
  refuser: (motif: string) => void;
}

const Ctx = React.createContext<{
  actif: boolean;
  porte: PastilleEchange | null;
  accepte: (b: PastilleEchange) => boolean;
} | null>(null);

export function EchangeContext({ ctx, children }: { ctx: ContexteEchange; children: React.ReactNode }) {
  const [porte, setPorte] = React.useState<PastilleEchange | null>(null);

  /* ⚠️ Les mêmes contraintes d'activation que la semaine, et pour la même
     raison : sans `distance` au pointeur et sans `delay` au toucher, le
     défilement de la page devient impossible sur tablette — chaque amorce de
     glissement vertical serait comprise comme une prise de pastille. Sur un
     mois dense, la pastille fait quinze pixels : on exige un geste franc. */
  const capteurs = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const accepte = React.useCallback((b: PastilleEchange) => {
    if (!porte || porte.documentId === b.documentId) return false;
    return ctx.valide(porte, b).ok;
  }, [porte, ctx]);

  const onDragStart = (e: DragStartEvent) => setPorte((e.active.data.current as PastilleEchange) || null);

  const onDragEnd = async (e: DragEndEvent) => {
    const a = (e.active.data.current as PastilleEchange) || null;
    setPorte(null);
    if (!a || !e.over) return; // lâchée hors de toute pastille : geste abandonné, rien à dire
    const b = (e.over.data.current as PastilleEchange) || null;
    if (!b || b.documentId === a.documentId) return;
    const v = ctx.valide(a, b);
    if (!v.ok) { ctx.refuser(v.motif || 'Échange impossible.'); return; }
    await ctx.echanger(a, b);
  };

  return (
    <Ctx.Provider value={{ actif: ctx.actif, porte, accepte }}>
      {/* La croix de retrait n'apparaît qu'au survol ou au focus ; sur un écran
          tactile (pas de survol), elle reste visible. */}
      <style>{`.pe-retirer { opacity: 0; transition: opacity .12s; }
        .pe-pastille:hover .pe-retirer, .pe-pastille:focus-within .pe-retirer { opacity: 1; }
        @media (hover: none) { .pe-retirer { opacity: 1; } }`}</style>
      <DndContext
        sensors={capteurs}
        onDragStart={onDragStart}
        onDragEnd={(e) => { void onDragEnd(e); }}
        onDragCancel={() => setPorte(null)}
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => {
              const a = active.data.current as PastilleEchange;
              return `Échange commencé avec ${a?.nom}, ${a?.posteLibelle}, le ${a?.date}.`;
            },
            onDragOver: ({ over }) => {
              const b = over?.data.current as PastilleEchange | undefined;
              return b ? `Au-dessus de ${b.nom}, ${b.posteLibelle}.` : 'Hors de toute pastille.';
            },
            onDragEnd: ({ active, over }) => {
              const a = active.data.current as PastilleEchange;
              const b = over?.data.current as PastilleEchange | undefined;
              return b ? `${a?.nom} déposée sur ${b.nom} : échange demandé.` : 'Échange abandonné.';
            },
            onDragCancel: () => 'Échange annulé, rien n’a changé.',
          },
        }}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {porte && (
            <div style={{
              padding: '4px 9px', borderRadius: 'var(--radius-sm)', fontSize: 12.5, fontWeight: 600,
              background: 'var(--cyan-50)', border: '1px solid var(--color-primary)',
              boxShadow: '0 6px 18px rgba(15,23,42,0.18)', cursor: 'grabbing', whiteSpace: 'nowrap',
            }}>⇄ {porte.nom} · {porte.posteLibelle}</div>
          )}
        </DragOverlay>
      </DndContext>
    </Ctx.Provider>
  );
}

/**
 * Une pastille saisissable ET déposable. Hors mode « Modifier », elle rend ses
 * enfants tels quels : aucun nœud en plus, aucune dépendance au contexte.
 */
export function PastilleEchangeable({ a, children, onRetirer, onRemplacer }: {
  a: PastilleEchange; children: React.ReactNode;
  /** Retire l'affectation d'un clic (croix au survol). Absent : pas de croix. */
  onRetirer?: (a: PastilleEchange) => void;
  /** Ouvre le choix d'une remplaçante (bouton au survol, coin opposé à la croix — RQ 01/11/2026). Absent : pas de bouton. */
  onRemplacer?: (a: PastilleEchange) => void;
}) {
  const dnd = React.useContext(Ctx);
  const actif = !!dnd?.actif;
  const g = useDraggable({ id: `ech:${a.documentId}`, data: a, disabled: !actif });
  const d = useDroppable({ id: `cible:${a.documentId}`, data: a, disabled: !actif });
  const ref = React.useCallback((n: HTMLElement | null) => { g.setNodeRef(n); d.setNodeRef(n); }, [g, d]);
  if (!actif) return <>{children}</>;

  const glisseEnCours = !!dnd?.porte;
  const estLaPrise = dnd?.porte?.documentId === a.documentId;
  const accepte = glisseEnCours && !estLaPrise && dnd!.accepte(a);
  return (
    <span ref={ref} {...g.listeners} {...g.attributes} className="pe-pastille"
      style={{
        display: 'block', position: 'relative', cursor: 'grab', touchAction: 'manipulation', borderRadius: 'var(--radius-xs)',
        /* La validité se voit DÈS LA PRISE, pas au survol : les cibles refusées
           s'estompent au lieu de rougir — trente pastilles rouges feraient lire
           un écran en faute, alors qu'il ne dit que ce qui est possible. */
        ...(estLaPrise ? { opacity: 0.35 } : {}),
        ...(glisseEnCours && !estLaPrise && !accepte ? { opacity: 0.35 } : {}),
        ...(accepte ? { outline: '2px dashed var(--color-primary)', outlineOffset: 1 } : {}),
        ...(accepte && d.isOver ? { outline: '2px solid var(--color-primary)', outlineOffset: 1, background: 'var(--cyan-50)' } : {}),
      }}>
      {children}
      {onRetirer && !glisseEnCours && (
        /* ⚠️ La croix NE DOIT PAS déclencher le glissé : le capteur du pointeur
           écoute le <span> parent, et celui du clavier démarre sur Entrée /
           Espace — on coupe les deux avant qu'ils ne remontent. */
        <button type="button" className="pe-retirer pm-ecran"
          aria-label={`Retirer ${a.nom} de « ${a.posteLibelle} » le ${a.date}`}
          title={`Retirer ${a.nom} de « ${a.posteLibelle} » — annulable`}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRetirer(a); }}
          style={{
            position: 'absolute', top: -5, insetInlineEnd: -5, width: 13, height: 13, padding: 0, lineHeight: '11px',
            fontSize: 10, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--surface-card)',
            borderRadius: '50%', background: 'var(--red-600)', color: 'var(--surface-card)', zIndex: 3,
          }}>×</button>
      )}
      {onRemplacer && !glisseEnCours && (
        /* Le pendant de la croix, coin opposé : placer une autre personne à
           la place de celle-ci. Mêmes coupe-circuits que la croix. */
        <button type="button" className="pe-retirer pm-ecran"
          aria-label={`Remplacer ${a.nom} sur « ${a.posteLibelle} » le ${a.date}`}
          title={`Placer une autre personne à la place de ${a.nom} — annulable`}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRemplacer(a); }}
          style={{
            position: 'absolute', top: -5, insetInlineStart: -5, width: 13, height: 13, padding: 0, lineHeight: '11px',
            fontSize: 9, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--surface-card)',
            borderRadius: '50%', background: 'var(--color-primary)', color: 'var(--surface-card)', zIndex: 3,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          {/* Une icône dessinée et non « ⇄ » : la police de l'écran n'a pas ce
              caractère, il s'affichait en carré vide (constaté le 01/11/2026). */}
          <I n="refresh" s={8} sw={3} />
        </button>
      )}
    </span>
  );
}
